'use client';

import * as React from 'react';
import dynamic from 'next/dynamic';

const GeometricCanvas = dynamic(() => import('./hero-geometric-canvas'), {
  ssr: false
});

class BackgroundBoundary extends React.Component<
  React.PropsWithChildren,
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/** Keep the static mint composition on phones and reduced-motion devices.
 * Three.js is loaded only for the desktop enhancement, not for the fallback. */
export function HeroGeometricBackground(): React.JSX.Element {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [dark, setDark] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const onFailure = React.useCallback(() => setFailed(true), []);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const media = window.matchMedia(
      '(min-width: 1024px) and (pointer: fine) and (prefers-reduced-motion: no-preference)'
    );
    let inView = false;
    const sync = () => {
      setEnabled(media.matches);
      setRunning(media.matches && inView && !document.hidden);
    };
    const syncTheme = () =>
      setDark(document.documentElement.classList.contains('dark'));
    const observer = new IntersectionObserver(([entry]) => {
      inView = !!entry?.isIntersecting;
      sync();
    });
    observer.observe(container);
    const themeObserver = new MutationObserver(syncTheme);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    media.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    sync();
    syncTheme();
    return () => {
      observer.disconnect();
      themeObserver.disconnect();
      media.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="lumnis-hero__texture"
    >
      {enabled && !failed && (
        <BackgroundBoundary>
          <GeometricCanvas
            running={running}
            dark={dark}
            onFailure={onFailure}
          />
        </BackgroundBoundary>
      )}
    </div>
  );
}
